import { useState, useEffect, useRef } from "react";
import Plot from "react-plotly.js";

import styles from "./statisticsPage.module.css";
import ImgBuilder from "../../assets/ImgBuilder";

import { useTranslation } from "react-i18next";
import {  Box} from '../../components/statisticsComponents';
import { getDashboard } from "../../api/api";
import { useUserData } from '../../functions/updateUserData';


const DashboardPage = () => {
  const { userData } = useUserData();
  const { t } = useTranslation();
  const loading = useRef(false);

  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    if (loading.current) return; // loading is true, no need for additional calls
    const fetchTopics = async () => {
      loading.current = true;
      try {
        const dashboard = await getDashboard();
        setDashboard(dashboard);  
      } catch (error) {
        console.error("Failed to fetch statistics about the user:", error);
      } finally{
        loading.current = false;
      }
    };


    fetchTopics();


    // set up interval (5 minutes = 300000 ms)
    const interval = setInterval(fetchTopics, 300000);

    // cleanup on unmount
    return () => clearInterval(interval);
    
  }, [userData]);

  const fontSize = window.innerHeight * 0.01;

  // const groupByInterval = (values, interval = 5) => {
  //   const groupedX = [];
  //   const groupedY = [];

  //   for (let i = 0; i < values.length; i += interval) {
  //     const chunk = values.slice(i, i + interval);
  //     const avg = chunk.reduce((a, b) => a + b, 0) / chunk.length;
  //     groupedX.push(i + interval); // bin endpoint (5, 10, 15…)
  //     groupedY.push(avg);
  //   }

  //   return { groupedX, groupedY };
  // }


  const movingAverage = (values, windowSize = 3) => {
    return values.map((_, idx, arr) => {
      const start = Math.max(0, idx - windowSize + 1);
      const subset = arr.slice(start, idx + 1);
      return subset.reduce((a, b) => a + b, 0) / subset.length;
    });
  }

  const binCounts = (seconds, interval = 5) => {
    if (seconds.length === 0) return { x: [], y: [] };

    const minSec = Math.floor(Math.min(...seconds) / interval) * interval;
    const maxSec = Math.ceil(Math.max(...seconds) / interval) * interval;

    const bins = [];
    const counts = [];

    for (let start = minSec; start < maxSec; start += interval) {
      bins.push(start + interval / 2); // midpoint for x-axis
      const count = seconds.filter(s => s >= start && s < start + interval).length;
      counts.push(count);
    }

    return { x: bins, y: counts };
  }

  const renderPlots = {
    speed: (values, index) => {
      const interval = 5; // seconds per bin
      const { x: binnedX, y: binnedY } = binCounts(values, interval);
      const smoothedY = movingAverage(binnedY, 7);

      return (
        <Plot
          key={index}
          data={[
            {
              x:binnedX ,
              y: smoothedY,
              type: "scatter",
              mode: "lines",
              line: { color: "rgba(255, 24, 24, 1)", shape: 'spline', smoothing: 1}, // smoothing: 1
              marker: { size: 4, color: "rgba(196, 37, 37, 0.6)" },
              fill: 'tozeroy',
              fillcolor: "rgba(196, 37, 37, 0.4)",
            },
          ]}
          layout={{
            xaxis: { title: { text: "Seconds", font: { size: fontSize * 1.5 } }, tickfont: { size: fontSize * 1.1}, autorange: true },
            yaxis: { title: { text: "Count", font: { size: fontSize * 1.5 } }, tickfont: { size: fontSize * 1.1} , autorange: true },
            margin: { l: 75, r: 40, t: 60, b: 75 },
          }}
          config={{ scrollZoom: false,displayModeBar: false, }}
          className={styles.barPlot}
        />
      );
    },

    change_mind: (values, index) => (
      <Plot
        key={index}
       data={[
          {
            x: values,
            type: "box",
            orientation: "h",
            boxpoints: "all",   // show all points
            pointpos: 0,        // place points on top of box
            jitter: 0.5,      // spread
            marker: { color: "#c42525", size: 6, opacity: 0.7 },
            line: { color: "#101010" },
            fillcolor: "#a097f7",
            name: "",
          },
        ]}
        layout={{
          xaxis: { title: { text: "Percentage (%)", font: { size: fontSize * 1.5 } }, tickfont: { size: fontSize * 1.1}, zeroline: false , autorange: true },
          margin: { l: 75, r: 40, t: 60, b: 75 },
        }}
        config={{ scrollZoom: false,displayModeBar: false, }}
        className={styles.barPlot}
      />
    ),

    trusting: (values, index) => {
      const roundedData = values.map(val => Math.round(val * 10) * 10);
      const categories = Array.from({ length: 11 }, (_, i) => i * 10);
      const counts = categories.map(cat => roundedData.filter(v => v === cat).length);

      return (
        <Plot
          key={index}
          data={[{
            x: categories,
            y: counts,
            type: "bar",
            marker: { color: "#a097f7" },
          }]}
          layout={{
            xaxis: { title: { text: "Percentage (%)", font: { size: fontSize * 1.5 } }, tickfont: { size: fontSize * 1.1}, dtick: 10, range: [0, 100] , autorange: true },
            yaxis: { title: { text: "Count", font: { size: fontSize * 1.5 } }, tickfont: { size: fontSize * 1.1}, dtick: 10, tickmode: "linear" , autorange: true },
            margin: { l: 75, r: 40, t: 60, b: 75 },
          }}
          config={{ scrollZoom: false,displayModeBar: false, }}
          className={styles.barPlot}
        />
      );
    }
  };

  return (
    <div className={styles.wrapper}>
      <img alt="bg" className={styles.bgImg} src={ImgBuilder.sbBackground}/>
      <div className={`${styles.container} ${styles.containerDash}`}>
        <p className={`${styles.pageName} ${styles.pageNameDash}`}>{t("dashboard.title")}</p>
        

        <div className={styles.dasboardsContainer}>

         {dashboard &&
          Object.entries(dashboard.topic_stats).map(([key, questions]) => (
            <div key={key} className={`${styles.statisticsBox} ${styles.statisticsBoxDash}`}>
              <p className={`${styles.statisticsTitle} ${styles.statisticsTitleDash}`}>
                {t(`news_page.topics.${key}`)}
              </p>
              <div className={styles.statisticsBoxContainer}>
                {Object.entries(questions).map(([key, values], index) => (
                  <Box text={t(`dashboard.${key}`)} dashboard={true}>
                    {(renderPlots[key])(values, index)}
                  </Box>
                ))}
              </div>
            </div>
          ))
        }

        </div>

      </div>

    </div>
  );
};

export default DashboardPage;
